import { type FC, type PropsWithChildren, useEffect, useState } from "react";

import { useHistory } from "react-router";

import { Button, Icon, Text } from "@alpacahq/ui/old";

import AmplitudeProvider from "~/AmplitudeProvider";
import { Path } from "~/v2/path";
import { useIsOptionsOnboardingAvailable } from "../../pages/dashboard/optionsOptIn/util";

const DOCS_LINK = "https://docs.alpaca.markets/docs/options-trading";
const TUTORIAL_LINK = "https://www.youtube.com/watch?v=B0Z7oCmr5nM";
const HIDE_OPTION_PROMOTION = "options_promo_1";

const LinkButton: FC<
  PropsWithChildren<{ href: string; onClick: () => void }>
> = ({ href, children, onClick }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener"
    className="flex w-full max-w-52 justify-center rounded-md border-2 border-yellow-500 py-2 font-medium text-black text-sm"
    onClick={onClick}
  >
    {children}
  </a>
);

type OptionsPromoProps = {
  isClosable?: boolean;
};

export const OptionsPromoCard: FC<OptionsPromoProps> = ({ isClosable }) => {
  const history = useHistory();
  const [isClosed, setIsClosed] = useState(true);

  const { isOptionsOnboardingAvailable, isOptionsApproved } =
    useIsOptionsOnboardingAvailable();

  const onCloseClicked = () => {
    window?.localStorage?.setItem(HIDE_OPTION_PROMOTION, "true");
    setIsClosed(true);
  };

  const onLinkClicked = (name: "docs" | "tutorial") => () =>
    AmplitudeProvider.dispatch(`algodash_options_promo_${name}_clicked`, {
      url: window.location.pathname,
    });

  const onStartApplicationClicked = () => {
    AmplitudeProvider.dispatch("algodash_options_promo_application_clicked", {
      url: window.location.pathname,
    });
    history.push(Path.ROUTE_OPTIONS_OPT_IN_FINANCIAL_PROFILE);
  };

  useEffect(() => {
    // Always display promo if not closable
    if (!isClosable) {
      setIsClosed(false);
      return;
    }

    // hide promo if user is options approved
    if (isOptionsApproved) {
      setIsClosed(true);
      return;
    }

    const wasClosed = !!window?.localStorage?.getItem(HIDE_OPTION_PROMOTION);
    setIsClosed(wasClosed);
  }, [isClosable, isOptionsApproved]);

  if (isClosed) {
    return null;
  }

  return (
    <div className="relative h-fit w-full max-w-96 rounded-lg border border-gray-200 p-4 min-[1224px]:max-w-full">
      <h2 className="m-0 font-bold text-base text-black">
        Trade Commission-free Options
      </h2>
      {isClosable && (
        <Button
          onClick={onCloseClicked}
          variant="ghost"
          className="absolute top-1 right-2"
        >
          <Icon name="XMark" />
        </Button>
      )}
      <div className="grid grid-cols-2 py-4">
        <div>
          <Text className="text-xs leading-3">
            Commission-free options trading is available on live account now.
          </Text>
          <Text className="pt-2 text-xs leading-3">
            Read our API documentation or watch our short video tutorial to
            learn more.
          </Text>
          {isOptionsOnboardingAvailable && (
            <Text className="pt-2 text-xs leading-3">
              Get approved to start trading now!
            </Text>
          )}
        </div>

        <div className="flex justify-center">
          <img
            src="./resources/images/options-ad.svg"
            alt="Call, Put, trade options commission-free"
          />
        </div>
      </div>
      <div className="flex justify-between gap-2">
        <LinkButton href={DOCS_LINK} onClick={onLinkClicked("docs")}>
          Read API Docs
        </LinkButton>
        <LinkButton href={TUTORIAL_LINK} onClick={onLinkClicked("tutorial")}>
          Watch Tutorial
        </LinkButton>
      </div>
      {isOptionsOnboardingAvailable && (
        <Button onClick={onStartApplicationClicked} className="mt-4 w-full">
          Start Application
        </Button>
      )}
    </div>
  );
};
